/**
 * Blob Storage Factory
 * 
 * Creates the appropriate blob storage service based on configuration.
 * Uses feature flags to determine which provider to use.
 */

import { ApiBlobStorageService } from './apiBlobStorage';
import { AzureBlobStorageService } from './azureBlobStorage';
import { S3BlobStorageService } from './s3BlobStorage';
import type { BlobStorageConfig, BlobStorageProvider, IBlobStorageService } from './types';

// Singleton instance
let _instance: IBlobStorageService | null = null;
let _currentProvider: BlobStorageProvider | null = null;

/**
 * Default configuration (uses API)
 */
const DEFAULT_CONFIG: BlobStorageConfig = {
  provider: 'api',
  defaultChunkSize: 5 * 1024 * 1024, // 5MB
  maxConcurrency: 4,
};

/**
 * Current configuration - can be updated at runtime
 */
let _config: BlobStorageConfig = { ...DEFAULT_CONFIG };

/**
 * Creates a blob storage service instance for the specified provider.
 */
function createService(provider: BlobStorageProvider): IBlobStorageService {
  switch (provider) {
    case 'azure':
      console.log('[BlobStorageFactory] Creating Azure Blob Storage service');
      return new AzureBlobStorageService({
        chunkSize: _config.defaultChunkSize,
        maxConcurrency: _config.maxConcurrency,
      });

    case 's3':
      console.log('[BlobStorageFactory] Creating S3 Blob Storage service');
      return new S3BlobStorageService({
        chunkSize: _config.defaultChunkSize,
        maxConcurrency: _config.maxConcurrency,
      });

    case 'api':
    default:
      console.log('[BlobStorageFactory] Creating API Blob Storage service');
      return new ApiBlobStorageService({
        maxConcurrency: _config.maxConcurrency,
      });
  }
}

/**
 * Gets the blob storage service instance.
 * Uses singleton pattern - creates instance on first call.
 * 
 * Call `configureBlobStorage()` before first use to set the provider.
 */
export function getBlobStorageService(): IBlobStorageService {
  if (!_instance || _currentProvider !== _config.provider) {
    _instance = createService(_config.provider);
    _currentProvider = _config.provider;
  }
  return _instance;
}

/**
 * Configures the blob storage service.
 * Should be called early in app initialization with feature flag values.
 * 
 * @param config - Storage configuration
 */
export function configureBlobStorage(config: Partial<BlobStorageConfig>): void {
  const previousProvider = _config.provider;
  _config = { ...DEFAULT_CONFIG, ...config };

  console.log('[BlobStorageFactory] Configuration updated:', {
    provider: _config.provider,
    previousProvider,
    baseUrl: _config.baseUrl,
    containerName: _config.containerName,
  });

  // Reset singleton if provider changed
  if (previousProvider !== _config.provider) {
    _instance = null;
    _currentProvider = null;
    console.log('[BlobStorageFactory] Provider changed, service will be recreated');
  }
}

/**
 * Gets the current blob storage configuration.
 */
export function getBlobStorageConfig(): Readonly<BlobStorageConfig> {
  return { ..._config };
}

/**
 * Gets the current provider type.
 */
export function getBlobStorageProvider(): BlobStorageProvider {
  return _config.provider;
}

/**
 * Resets the blob storage service (useful for testing).
 */
export function resetBlobStorage(): void {
  _instance = null;
  _currentProvider = null;
  _config = { ...DEFAULT_CONFIG };
  console.log('[BlobStorageFactory] Reset to default configuration');
}

/**
 * Helper to configure blob storage from application settings.
 * Call this after loading app settings with feature flags.
 * 
 * @param features - Array of enabled feature flags
 * @param blobConfig - Optional blob-specific config from app settings
 */
export function configureBlobStorageFromFeatures(
  features: string[],
  blobConfig?: {
    provider?: string;
    baseUrl?: string;
    containerName?: string;
  }
): void {
  // Determine provider from feature flags or explicit config
  let provider: BlobStorageProvider = 'api';

  if (blobConfig?.provider) {
    // Explicit provider config takes precedence
    provider = blobConfig.provider as BlobStorageProvider;
  } else if (features.includes('BlobStorageS3')) {
    provider = 's3';
  } else if (features.includes('BlobStorageAzure')) {
    provider = 'azure';
  }

  configureBlobStorage({
    provider,
    baseUrl: blobConfig?.baseUrl,
    containerName: blobConfig?.containerName,
  });
}

